/**
 * Catalog builder: one or two Office.js syncs that enumerate the
 * workbook's structural metadata (sheet names + used-range bounds,
 * tables, defined names) and assemble a WorkbookCatalogs ready for the
 * chunked reader.
 *
 * Catalogs are workbook-wide regardless of scan scope — formulas
 * reference any sheet, any table, any defined name. The scope only
 * affects which sheets (or which subset of one sheet) the chunked
 * reader will actually ingest, captured here as `ingestionTargets`.
 *
 * Interning dicts (r1c1Dict, numFmtDict) are returned empty; the
 * SheetSink fills them during ingestion.
 *
 * spillCatalog is returned with empty per-sheet indexes. Building real
 * spill indexes is Phase 5a per the architecture. Until then, the
 * SheetSink classifies spill interiors as CONSTANT — a known imprecision
 * that the hardcode-in-formula-band rule will need to account for.
 */

import type {
  NameEntry,
  ScanScope,
  SheetBounds,
  SheetMetadata,
  SheetVisibility,
  SpillSheetIndex,
  TableEntry,
  WorkbookCatalogs,
} from "../types";
import { parseSimpleRangeRef } from "./refParser";

/**
 * Per-sheet ranges the chunked reader will ingest under the current
 * scope. For "workbook", one entry per sheet with non-empty used range.
 * For "sheet", a single entry for the active sheet's used range. For
 * "selection", a single entry whose bounds are the user's selected
 * range (which may be smaller than the sheet's used range).
 */
export interface IngestionTarget {
  sheetId: number;
  name: string;
  bounds: SheetBounds;
}

export interface CatalogBuildResult {
  catalogs: WorkbookCatalogs;
  workbookName: string;
  fingerprint: string;
  ingestionTargets: IngestionTarget[];
}

/** Empty per-sheet spill index — see header comment. */
const EMPTY_SPILL_INDEX: SpillSheetIndex = {
  contains: () => false,
  isAnchor: () => false,
};

/**
 * Map Office.js sheet visibility (enum value OR bare string literal —
 * modern @types/office-js accepts both forms) to the model's string
 * union. String comparison covers all callable shapes uniformly.
 */
function mapSheetVisibility(v: unknown): SheetVisibility {
  if (v === "Visible" || v === Excel.SheetVisibility.visible) return "visible";
  if (v === "Hidden" || v === Excel.SheetVisibility.hidden) return "hidden";
  if (v === "VeryHidden" || v === Excel.SheetVisibility.veryHidden) return "veryHidden";
  return "unknown";
}

/**
 * Enumerate workbook structure and assemble catalogs + ingestion plan.
 * Runs inside its own `Excel.run`; safe to call standalone.
 */
export async function buildCatalogs(scope: ScanScope): Promise<CatalogBuildResult> {
  return Excel.run(async (ctx) => {
    const wb = ctx.workbook;

    // ===== Sync 1: workbook-level enumerations =====
    wb.load("name");
    const sheets = wb.worksheets;
    sheets.load("items/name,items/position,items/visibility");
    const tables = wb.tables;
    tables.load("items/name");
    const wbNames = wb.names;
    wbNames.load("items/name,items/formula,items/type");

    // Scope-dependent: active sheet / selected range
    const activeSheet =
      scope === "sheet" || scope === "selection"
        ? wb.worksheets.getActiveWorksheet()
        : null;
    if (activeSheet) activeSheet.load("name");

    const selectedRange = scope === "selection" ? wb.getSelectedRange() : null;
    if (selectedRange) {
      selectedRange.load([
        "rowIndex",
        "columnIndex",
        "rowCount",
        "columnCount",
        "worksheet/name",
      ]);
    }

    // eslint-disable-next-line office-addins/no-context-sync-in-loop
    await ctx.sync();

    // ===== Sync 2: per-sheet used ranges, table details, sheet-scoped names =====
    const sheetItems = sheets.items;
    const usedRanges = sheetItems.map((s) => s.getUsedRangeOrNullObject(true));
    for (const ur of usedRanges) {
      ur.load(["rowIndex", "columnIndex", "rowCount", "columnCount", "isNullObject"]);
    }

    const tableItems = tables.items;
    const tableRanges = tableItems.map((t) => t.getRange());
    for (const tr of tableRanges) {
      tr.load(["rowIndex", "columnIndex", "rowCount", "columnCount"]);
    }
    for (const t of tableItems) {
      t.worksheet.load("name");
      t.columns.load("items/name,items/index");
    }

    const sheetNameCollections = sheetItems.map((s) => {
      const ns = s.names;
      ns.load("items/name,items/formula,items/type");
      return ns;
    });

    await ctx.sync();

    // ===== Assemble catalogs =====

    const sheetNameDict = new Map<string, number>();
    const sheetsBySheetId = new Map<number, SheetMetadata>();

    for (let id = 0; id < sheetItems.length; id++) {
      const sheet = sheetItems[id];
      const ur = usedRanges[id];
      const bounds: SheetBounds = ur.isNullObject
        ? { startRow: 0, startCol: 0, rows: 0, cols: 0 }
        : {
            startRow: ur.rowIndex,
            startCol: ur.columnIndex,
            rows: ur.rowCount,
            cols: ur.columnCount,
          };
      sheetNameDict.set(sheet.name, id);
      sheetsBySheetId.set(id, {
        name: sheet.name,
        bounds,
        position: sheet.position,
        visibility: mapSheetVisibility(sheet.visibility),
      });
    }

    // ---- Tables ----
    const tablesCatalog = new Map<string, TableEntry>();
    for (let i = 0; i < tableItems.length; i++) {
      const t = tableItems[i];
      const tr = tableRanges[i];
      const wsName = t.worksheet.name;
      const sheetId = sheetNameDict.get(wsName);
      if (sheetId === undefined) continue; // sheet vanished between syncs; defensive

      const columns = new Map<string, number>();
      for (const col of t.columns.items) {
        // Excel column indices are 1-based externally; store 0-based to match the rest.
        columns.set(col.name, col.index);
      }

      tablesCatalog.set(t.name, {
        sheetId,
        bounds: {
          startRow: tr.rowIndex,
          startCol: tr.columnIndex,
          rows: tr.rowCount,
          cols: tr.columnCount,
        },
        columns,
      });
    }

    // ---- Defined names (workbook + sheet-scoped) ----
    // For Phase B initial: when a workbook-scoped name and a sheet-scoped name
    // share the same string, the last write wins. The architecture allows
    // sheet-scoped to shadow workbook-scoped; build order here puts workbook
    // first, sheets second, so sheet-scoped wins — matching the spec for the
    // single-conflict case. Multiple sheet-scoped names with the same string
    // collapse to one entry (rare; documented limitation).
    const definedNamesCatalog = new Map<string, NameEntry>();

    for (const n of wbNames.items) {
      const entry = parseDefinedName(n, sheetNameDict, null);
      if (entry) definedNamesCatalog.set(n.name, entry);
    }
    for (let id = 0; id < sheetItems.length; id++) {
      for (const n of sheetNameCollections[id].items) {
        const entry = parseDefinedName(n, sheetNameDict, id);
        if (entry) definedNamesCatalog.set(n.name, entry);
      }
    }

    // ---- Empty spill catalog (one per sheet) ----
    const spillCatalog = new Map<number, SpillSheetIndex>();
    for (const id of Array.from(sheetsBySheetId.keys())) {
      spillCatalog.set(id, EMPTY_SPILL_INDEX);
    }

    const catalogs: WorkbookCatalogs = {
      sheetNameDict,
      sheetsBySheetId,
      tablesCatalog,
      definedNamesCatalog,
      spillCatalog,
      r1c1Dict: new Map(),
      r1c1Strings: [],
      formulaA1Dict: new Map(),
      formulaA1Strings: [],
      numFmtDict: new Map(),
      numFmtStrings: [],
      textValueDict: new Map(),
      textValueStrings: [],
    };

    // ===== Resolve ingestion targets per scope =====
    const ingestionTargets = computeIngestionTargets(
      scope,
      sheetsBySheetId,
      activeSheet ? activeSheet.name : undefined,
      selectedRange
        ? {
            sheetName: selectedRange.worksheet.name,
            startRow: selectedRange.rowIndex,
            startCol: selectedRange.columnIndex,
            rows: selectedRange.rowCount,
            cols: selectedRange.columnCount,
          }
        : undefined,
      sheetNameDict
    );

    const workbookName = wb.name;
    const fingerprint = computeFingerprint(workbookName, scope, catalogs);

    return { catalogs, workbookName, fingerprint, ingestionTargets };
  });
}

// ============================================================================
// Internal helpers
// ============================================================================

/**
 * Resolve an Excel.NamedItem to a NameEntry, or null if its formula
 * isn't a simple range reference (e.g. it's a formula expression). The
 * `ownerSheetId` is null for workbook-scoped names, the sheet id for
 * sheet-scoped.
 */
function parseDefinedName(
  n: Excel.NamedItem,
  sheetNameDict: Map<string, number>,
  ownerSheetId: number | null
): NameEntry | null {
  // Only range-typed names can resolve to bounds. Others are string/numeric
  // constants or computed expressions that don't represent a range.
  // Excel.NamedItemType uses lowercase string values ("range", "string", …).
  if (n.type !== "Range" && n.type !== ("range" as Excel.NamedItemType)) {
    // The two forms guard against differences across Office.js versions.
    return null;
  }

  const parsed = parseSimpleRangeRef(n.formula);
  if (!parsed) return null;

  const referencedSheetId = sheetNameDict.get(parsed.sheetName);
  if (referencedSheetId === undefined) return null;

  return {
    sheetId: ownerSheetId,
    bounds: {
      startRow: parsed.startRow,
      startCol: parsed.startCol,
      rows: parsed.rows,
      cols: parsed.cols,
    },
  };
}

interface SelectionInfo {
  sheetName: string;
  startRow: number;
  startCol: number;
  rows: number;
  cols: number;
}

function computeIngestionTargets(
  scope: ScanScope,
  sheetsBySheetId: Map<number, SheetMetadata>,
  activeSheetName: string | undefined,
  selection: SelectionInfo | undefined,
  sheetNameDict: Map<string, number>
): IngestionTarget[] {
  if (scope === "workbook") {
    const targets: IngestionTarget[] = [];
    for (const [id, meta] of Array.from(sheetsBySheetId)) {
      if (meta.bounds.rows === 0 || meta.bounds.cols === 0) continue;
      targets.push({ sheetId: id, name: meta.name, bounds: meta.bounds });
    }
    return targets;
  }

  if (scope === "sheet") {
    if (!activeSheetName) return [];
    const id = sheetNameDict.get(activeSheetName);
    if (id === undefined) return [];
    const meta = sheetsBySheetId.get(id)!;
    if (meta.bounds.rows === 0 || meta.bounds.cols === 0) return [];
    return [{ sheetId: id, name: meta.name, bounds: meta.bounds }];
  }

  // selection
  if (!selection) return [];
  const id = sheetNameDict.get(selection.sheetName);
  if (id === undefined) return [];
  if (selection.rows === 0 || selection.cols === 0) return [];
  return [
    {
      sheetId: id,
      name: selection.sheetName,
      bounds: {
        startRow: selection.startRow,
        startCol: selection.startCol,
        rows: selection.rows,
        cols: selection.cols,
      },
    },
  ];
}

/**
 * Build a stable fingerprint of the workbook's structural state.
 * Changes when sheets/tables/names are added, removed, renamed, or
 * resized. Does NOT change when cell values change — for cell-level
 * invalidation, callers must explicitly invoke `invalidateWorkbookModel`.
 */
function computeFingerprint(
  workbookName: string,
  scope: ScanScope,
  catalogs: WorkbookCatalogs
): string {
  const sheetsPart = Array.from(catalogs.sheetsBySheetId.values())
    .map(
      (s) =>
        `${s.name}@${s.bounds.startRow},${s.bounds.startCol}/${s.bounds.rows}x${s.bounds.cols}#${s.position}~${s.visibility}`
    )
    .sort()
    .join(",");

  const tablesPart = Array.from(catalogs.tablesCatalog.entries())
    .map(
      ([name, t]) =>
        `${name}:${t.sheetId}@${t.bounds.startRow},${t.bounds.startCol}/${t.bounds.rows}x${t.bounds.cols}`
    )
    .sort()
    .join(",");

  const namesPart = Array.from(catalogs.definedNamesCatalog.entries())
    .map(
      ([name, e]) =>
        `${name}:${e.sheetId ?? "wb"}@${e.bounds.startRow},${e.bounds.startCol}/${e.bounds.rows}x${e.bounds.cols}`
    )
    .sort()
    .join(",");

  return `wb=${workbookName}|scope=${scope}|sheets=[${sheetsPart}]|tables=[${tablesPart}]|names=[${namesPart}]`;
}
